import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { productsAPI } from '../api/vendly';
import { useCart } from '../context/CartContext';
import ReviewSection from '../components/ReviewSection';
import styles from './ProductDetailPage.module.css';

const formatPrice = (n) => `₦${Number(n).toLocaleString()}`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const { add } = useCart();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [added, setAdded] = useState(false);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setLoading(true);
    setError('');
    productsAPI.get(id)
      .then(res => {
        setProduct(res.data.product);
        // Fetch related products in same category
        return productsAPI.list({ category: res.data.product.category, per_page: 4 });
      })
      .then(res => {
        setRelated(res.data.products.filter(p => p.id !== Number(id)).slice(0, 4));
      })
      .catch(() => setError('Product not found or backend is offline.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) add(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (loading) return (
    <div className={styles.page}>
      <div className="container">
        <div className={styles.loadingMain}>
          <div className={styles.skelImg} />
          <div className={styles.skelInfo}>
            <div className={styles.skelLine} style={{ width: '60%', height: 14 }} />
            <div className={styles.skelLine} style={{ width: '90%', height: 32 }} />
            <div className={styles.skelLine} style={{ width: '40%', height: 28 }} />
            <div className={styles.skelLine} style={{ width: '100%', height: 80 }} />
          </div>
        </div>
      </div>
    </div>
  );

  if (error || !product) return (
    <div className={styles.notFound}>
      <span>😕</span>
      <h2>{error || 'Product not found'}</h2>
      <Link to="/shop">← Back to shop</Link>
    </div>
  );

  const discount = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : null;

  return (
    <div className={styles.page}>
      <div className="container">
        {/* Breadcrumb */}
        <nav className={styles.breadcrumb}>
          <Link to="/home">Home</Link>
          <span>›</span>
          <Link to="/shop">Shop</Link>
          <span>›</span>
          <Link to={`/shop?cat=${product.category}`}>{product.category}</Link>
          <span>›</span>
          <span>{product.name}</span>
        </nav>

        {/* Main */}
        <div className={styles.main}>
          <div className={styles.imageWrap}>
            <img src={product.image} alt={product.name} className={styles.image} />
            {discount && <span className={styles.discount}>-{discount}%</span>}
          </div>

          <div className={styles.info}>
            {product.badge && <span className={styles.badge}>{product.badge}</span>}
            <h1 className={styles.name}>{product.name}</h1>

            <div className={styles.rating}>
              {[1,2,3,4,5].map(s => (
                <svg key={s} width="14" height="14" viewBox="0 0 24 24"
                  fill={s <= Math.round(product.rating) ? 'var(--accent)' : 'var(--border)'} stroke="none">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
              ))}
              <span className={styles.ratingNum}>{product.rating}</span>
              <span className={styles.ratingCount}>({Number(product.reviews).toLocaleString()} reviews)</span>
            </div>

            <div className={styles.pricing}>
              <span className={styles.price}>{formatPrice(product.price)}</span>
              {product.originalPrice && (
                <span className={styles.original}>{formatPrice(product.originalPrice)}</span>
              )}
            </div>

            {/* Stock indicator */}
            <div className={styles.stockWrap}>
              <span className={`${styles.stockDot} ${product.stock > 0 ? styles.stockIn : styles.stockOut}`} />
              <span className={styles.stockLabel}>
                {product.stock > 10 ? 'In stock' : product.stock > 0 ? `Only ${product.stock} left!` : 'Out of stock'}
              </span>
            </div>

            <p className={styles.description}>{product.description}</p>

            <div className={styles.features}>
              {['Authentic product', 'Secure checkout', '7-day returns', 'Fast delivery'].map(f => (
                <div key={f} className={styles.feature}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span>{f}</span>
                </div>
              ))}
            </div>

            <div className={styles.actions}>
              <div className={styles.qtyWrap}>
                <button className={styles.qtyBtn} onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                <span className={styles.qty}>{qty}</span>
                <button
                  className={styles.qtyBtn}
                  onClick={() => setQty(q => Math.min(product.stock, q + 1))}
                  disabled={qty >= product.stock}
                >+</button>
              </div>
              <button
                className={`${styles.addBtn} ${added ? styles.addedBtn : ''}`}
                onClick={handleAdd}
                disabled={product.stock === 0}
              >
                {added ? '✓ Added to cart!' : product.stock === 0 ? 'Out of stock' : 'Add to cart'}
              </button>
            </div>

            <div className={styles.delivery}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
              </svg>
              <span>Free delivery on orders over ₦20,000</span>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className={styles.related}>
            <h2 className={styles.relatedTitle}>You may also like</h2>
            <div className={styles.relatedGrid}>
              {related.map(p => (
                <Link key={p.id} to={`/product/${p.id}`} className={styles.relatedCard}>
                  <img src={p.image} alt={p.name} className={styles.relatedImg} />
                  <div className={styles.relatedInfo}>
                    <p className={styles.relatedName}>{p.name}</p>
                    <p className={styles.relatedPrice}>{formatPrice(p.price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Reviews */}
        {product && <ReviewSection productId={product.id} />}

      </div>
    </div>
  );
}
